import Peer from 'peerjs';

import { config } from '../config';
import { uuid } from '../utils/code-gen';
import { createPeer } from '../utils/peerjs';
import {
  isNetworkDataResponse,
  NetworkDataResponse,
  NetworkDataType,
} from './NetworkDataTransfers';
import { ignoredNetworkDataDebugMessageTypes } from '../constants';

export const idPrefix = 'tpg-';

export abstract class NetworkHandler {
  dataListeners: {
    id: string;
    listener: (data: NetworkDataResponse) => void;
    type: NetworkDataType | null;
  }[] = [];
  localConnection: Peer;
  peerConnectionError: Error | null = null;
  networkReady = false;

  constructor(uuid: string) {
    this.localConnection = createPeer(idPrefix + uuid, this.peerJsErrorHandler);
  }

  peerJsErrorHandler = (error: Error) => {
    // TODO: Maybe add an error toast here
    console.error(error);
    this.peerConnectionError = error;
  };

  waitForOpen = async () => {
    if (this.localConnection.open) return;
    if (this.peerConnectionError !== null) throw this.peerConnectionError.message;
    return await new Promise<void>((res, rej) => {
      this.localConnection.on('open', (e) => res());
      this.localConnection.on('error', (e) => {
        this.localConnection.off('error'); // ? Is this necessary?
        rej('PeerJS: ' + e.message);
      });

      this.localConnection.on('disconnected', (e) => {
        this.localConnection.reconnect();
      });
    });
  };

  /**
   * returns function to remove listener
   */
  addDataListener(listener: (data: NetworkDataResponse) => void): () => void;
  addDataListener<T extends NetworkDataType>(
    listener: (data: NetworkDataResponse & { type: T }) => void,
    type: T,
  ): () => void;
  addDataListener(
    listener: (data: NetworkDataResponse) => void,
    type: NetworkDataType | null = null,
  ): () => void {
    const id = uuid();
    const dataListener = { id, listener, type };
    this.dataListeners.push(dataListener);

    return () => {
      const index = this.dataListeners.findIndex((dL) => dL.id === dataListener.id);
      if (index !== -1) this.dataListeners.splice(index, 1);
    };
  }

  getDataListeners<T extends NetworkDataType>(
    type: T,
  ): {
    listener: (data: NetworkDataResponse) => void;
    type: T;
  }[] {
    return this.dataListeners.filter((dataListeners) => dataListeners.type === type) as {
      id: string;
      listener: (data: NetworkDataResponse) => void;
      type: T;
    }[];
  }

  onData = (data: unknown) => {
    if (!isNetworkDataResponse(data)) {
      throw new Error('Invalid peer data transfer');
    }

    if (config.debugMode) {
      if (!ignoredNetworkDataDebugMessageTypes.includes(data.type)) {
        console.info('[TPG] Received P2P data', data);
      }
    }

    this.dataListeners
      .filter(({ type }) => type === null || type === data.type)
      .forEach(({ listener }) => listener(data));
  };
}
